/**
 * ClientSearchField — the workflow surfaces' client search: ora's ONE client
 * search control (`ClientQuicklinkDropdown`, the header search) in its compact,
 * in-page form, speaking the workflow `ClientSearchResult` contract.
 *
 * Story 28633 retired the workflow-only `ClientSearchDropdown`. The client box
 * (`ClientConfirmSection`) and the client tile (`ClientContextTile`) both render
 * this, so the adapter and the layout live once:
 *   - rows show city/state/zip, location and client type (ora's row), so two
 *     same-named clients read differently;
 *   - `compact` sizes it to the pane's one control height and keeps it in the
 *     page's flow (the header's fixed height let it overlap what followed).
 *
 * Shared — no Office.js, no Remix import.
 */
import ClientQuicklinkDropdown from "~/common/components/ClientQuicklinkDropdown";
import { fromClientOption, toClientOption } from "~/common/workflows/clientSearchOption";
import type { ClientSearchResult } from "~/common/workflows/types/client";

/** ora's header prompt, word for word. */
export const CLIENT_SEARCH_PROMPT = "Start typing client name or address";

export interface ClientSearchFieldProps {
  id: string;
  searchClients: (query: string) => Promise<ClientSearchResult[]>;
  /** A picked client. A cleared input is not a pick and never calls this. */
  onPick: (result: ClientSearchResult) => void;
  /** Only when the person just asked to search (e.g. "Change"); never on load. */
  autoFocus?: boolean;
}

export function ClientSearchField({
  id,
  searchClients,
  onPick,
  autoFocus = false,
}: ClientSearchFieldProps) {
  return (
    <ClientQuicklinkDropdown
      id={id}
      label={CLIENT_SEARCH_PROMPT}
      compact
      autoFocus={autoFocus}
      searchClients={(query) =>
        searchClients(query).then((results) => results.map(toClientOption))
      }
      onSelect={(option) => {
        if (option && typeof option === "object") onPick(fromClientOption(option));
      }}
      onBlurAction={() => undefined}
    />
  );
}

export default ClientSearchField;
